import {
  all,
  call,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import {requestContext} from '../../../../../../common/requestContext';
import {AppState} from '../../../../../../state/store';
import {events} from '../constant/textViewerSagaEvents';
import {
  setCurrentHtmlInfo as _setCurrentHtmlInfo,
  clearChapterChangeDirection,
  setChapterCount,
  setChapterHtmlInfo,
  setChapterPageCountList,
  setCurrentPage,
  setCurrentRate,
  setIsContentLoaded,
  setIsDataLoaded,
  setIsDone,
  setIsDrawing,
  setIsPageCalculating,
  setMetaInfo,
  setPreloadedHtmlInfo,
  setTextViewerError,
} from '../state/textViewerSlice';
import {setIsViewerEndExposed} from '../state/viewerEndSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {CountTriggerTextViewerCoreOption} from '../type/CountTriggerTextViewerCoreOption.interface';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {EventType} from '../type/textViewerSagaEventType.enum';
import {checkIsViewerEnd} from './checkIsViewerEnd.saga';
import {checkTextViewerIsDone} from './checkTextViewerIsDone';
import {convertToLastViewRate} from './convertToLastViewRate';
import {countCurrentPageCount} from './countCurrentPageCount.saga';
import {getPageCount} from './getPageCount';
import {getTextViewerCoreOption} from './getTextViewerCoreOption.saga';
import {loadTextViewerData} from './loadTextViewerData.saga';
import {restoreFromLastViewRate} from './restoreFromLastViewRate';
import {restoreLastViewInfo} from './restoreLastViewInfo.saga';
import {retrieveUrls} from './retrieveUrls.saga';
import {setCurrentHtmlInfo} from './setCurrentHtmlInfo.saga';

function* textViewerEntered({
  payload,
}: ReturnType<typeof events.textViewerEntered>) {
  const {seriesId, productId, isPreview, viewerInfoData} = payload;

  if (!isPreview) {
    yield call(restoreLastViewInfo, {productId, viewerInfoData});
    yield put(setIsViewerEndExposed(true));
    yield put(setIsViewerMenuExposed({isExposed: true}));
  } else {
    yield put(setIsViewerEndExposed(false));
    yield put(setIsViewerMenuExposed({isExposed: false}));
  }

  yield put(
    events.textViewerDataLoadRequired({seriesId, productId, viewerInfoData})
  );
}

function* textViewerDataLoadRequired({
  payload,
}: ReturnType<typeof events.textViewerDataLoadRequired>) {
  const {seriesId, productId, viewerInfoData} = payload;

  try {
    yield call(retrieveUrls, {productId, viewerInfoData});
    yield call(loadTextViewerData, {seriesId, productId});

    yield put(events.textViewerDataLoadCompleted({productId}));
  } catch (error) {
    yield put(events.textViewerErrorOccurred({productId, error}));
  }
}

function* textViewerMetaLoadCompleted({
  payload,
}: ReturnType<typeof events.textViewerMetaLoadCompleted>) {
  const {productId, metaInfo, loadedInfo} = payload;

  if (!metaInfo || !loadedInfo) {
    return;
  }

  yield put(setMetaInfo({productId, metaInfo}));
  yield put(
    setChapterCount({
      productId,
      chapterCount: loadedInfo.chapterCount,
    })
  );

  const {rate, currentChapterId} = yield select(
    (state: AppState) => state.textViewer.viewData[productId]
  );
  yield put(
    setIsDone({
      productId,
      isDone: checkTextViewerIsDone({
        rate,
        currentChapterId,
        chapterCount: loadedInfo.chapterCount,
      }),
    })
  );
}

function* textViewerPreloadCompleted({
  payload,
}: ReturnType<typeof events.textViewerPreloadCompleted>) {
  const {productId, chapterId, htmlInfo} = payload;
  const currentChapterId: number | undefined = yield select(
    (state: AppState) => state.textViewer.viewData[productId]?.currentChapterId
  );

  if (!htmlInfo) {
    return;
  }
  yield put(setPreloadedHtmlInfo({productId, chapterId, htmlInfo}));
  if (currentChapterId !== undefined && chapterId === currentChapterId) {
    yield put(_setCurrentHtmlInfo({productId, htmlInfo}));
  }
}

function* textViewerChapterLoadCompleted({
  payload,
}: ReturnType<typeof events.textViewerChapterLoadCompleted>) {
  const {productId, chapterId, htmlInfo} = payload;
  const currentChapterId: number | undefined = yield select(
    (state: AppState) => state.textViewer.viewData[productId]?.currentChapterId
  );

  if (!htmlInfo) {
    return;
  }

  yield put(
    setChapterHtmlInfo({
      productId,
      chapterId,
      htmlInfo,
    })
  );
  if (currentChapterId !== undefined && chapterId === currentChapterId) {
    yield put(_setCurrentHtmlInfo({productId, htmlInfo}));
  }
}

function* textViewerDataLoadCompleted({
  payload,
}: ReturnType<typeof events.textViewerDataLoadCompleted>) {
  const {productId} = payload;
  yield call(checkIsViewerEnd, {productId});
  yield put(setIsDataLoaded({productId, loaded: true}));
}

function* currentChapterChanged({
  payload,
}: ReturnType<typeof events.currentChapterChanged>) {
  const {productId} = payload;
  yield call(setCurrentHtmlInfo, {productId});
  yield call(checkIsViewerEnd, {productId});
}

function* textViewerContentLoadRequired({
  payload,
}: ReturnType<typeof events.textViewerContentLoadRequired>) {
  const {productId} = payload;

  yield put(setIsContentLoaded({productId, loaded: false}));

  const [redrawContentCompletedAction, _]: [
    ReturnType<typeof events.redrawContentCompleted>,
    any,
  ] = yield all([
    take(EventType.RedrawContentCompleted),
    take(EventType.AllPageCountCompleted),
  ]);

  const {wrapperStartY, wrapperBottomY} = redrawContentCompletedAction.payload;

  yield put(
    events.textViewerContentLoadCompleted({
      productId,
      wrapperStartY,
      wrapperBottomY,
    })
  );
}

function* currentPageCountRequired({
  payload,
}: ReturnType<typeof events.currentPageCountRequired>) {
  const {productId, size, isPreview} = payload;

  yield put(setIsPageCalculating({productId, isPageCalculating: true}));
  yield call(countCurrentPageCount, {productId, size, isPreview});
  yield put(setIsPageCalculating({productId, isPageCalculating: false}));
}

function* allPageCountRequired({
  payload,
}: ReturnType<typeof events.allPageCountRequired>) {
  const {productId, size, isPreview} = payload;
  const {
    htmlInfoList,
    currentPage,
    currentPageCount,
    currentChapterId,
    readType,
  } = yield select((state: AppState) => ({
    htmlInfoList: state.textViewer.viewData[productId]?.htmlInfoList,
    currentPage: state.textViewer.viewData[productId]?.currentPage,
    currentPageCount: state.textViewer.viewData[productId]?.currentPageCount,
    currentChapterId: state.textViewer.viewData[productId]?.currentChapterId,
    readType: state.textViewer.settings.readType,
  }));

  yield put(setIsPageCalculating({productId, isPageCalculating: true}));

  const prevRate =
    currentPage && currentPageCount
      ? convertToLastViewRate({
          position: currentPage,
          total: currentPageCount,
        })
      : null;

  const newPageCountList = [];
  let newCurrentChapterPageCount = undefined;

  const option: CountTriggerTextViewerCoreOption = yield call(
    getTextViewerCoreOption,
    {
      size,
      isMobile: requestContext.requestInfo.display.isMobile,
    }
  );

  for (let i = 0; i < htmlInfoList.length; i++) {
    const htmlInfo = htmlInfoList[i];
    if (!htmlInfo) {
      continue;
    }
    const pageCount: number = yield call(getPageCount, {
      htmlInfo,
      option,
      readType: isPreview ? TextViewerReadType.Scroll : readType,
    });

    newPageCountList.push({chapterId: i, pageCount});

    if (i === currentChapterId) {
      newCurrentChapterPageCount = pageCount;
    }
  }

  yield put(
    setChapterPageCountList({productId, pageCountList: newPageCountList})
  );

  yield call(countCurrentPageCount, {productId, size, isPreview});

  if (prevRate && newCurrentChapterPageCount) {
    const page = restoreFromLastViewRate({
      rate: prevRate,
      total: newCurrentChapterPageCount,
    });
    yield put(setCurrentPage({productId, page}));
  }

  yield put(setIsPageCalculating({productId, isPageCalculating: false}));
  yield put(events.allPageCountCompleted());
}

function* textViewerContentLoadCompleted({
  payload,
}: ReturnType<typeof events.textViewerContentLoadCompleted>) {
  const {productId, wrapperStartY, wrapperBottomY} = payload;
  const {readType} = yield select((state: AppState) => ({
    readType: state.textViewer.settings.readType,
  }));
  yield put(setIsContentLoaded({productId, loaded: true}));

  if (readType === TextViewerReadType.Page) {
    yield put(events.applyPageViewerRateRequired({productId}));
  } else {
    yield put(
      events.applyScrollViewerRateRequired({
        productId,
        wrapperStartY: wrapperStartY ?? 0,
        wrapperBottomY: wrapperBottomY ?? 0,
      })
    );
  }
}

function* applyScrollViewerRateRequired({
  payload,
}: ReturnType<typeof events.applyScrollViewerRateRequired>) {
  const {productId, wrapperStartY, wrapperBottomY} = payload;
  const {currentRate, currentPageCount} = yield select((state: AppState) => ({
    currentRate: state.textViewer.viewData[productId]?.currentRate,
    currentPageCount: state.textViewer.viewData[productId]?.currentPageCount,
  }));

  if (currentRate !== 0 && currentPageCount) {
    const pageHeight = window.innerHeight;
    const position = restoreFromLastViewRate({
      rate: currentRate,
      total: wrapperBottomY - wrapperStartY,
    });
    const windowStartY = wrapperStartY + position - pageHeight;

    window.scroll(0, windowStartY);
  }

  yield put(
    events.applyScrollViewerRateCompleted({
      productId,
      wrapperStartY,
      wrapperBottomY,
    })
  );
}

function* applyPageViewerRateRequired({
  payload,
}: ReturnType<typeof events.applyPageViewerRateRequired>) {
  const {productId} = payload;
  const {currentRate, currentPageCount} = yield select((state: AppState) => ({
    currentRate: state.textViewer.viewData[productId]?.currentRate,
    currentPageCount: state.textViewer.viewData[productId]?.currentPageCount,
  }));

  if (currentRate !== 0 && currentPageCount) {
    const page = restoreFromLastViewRate({
      rate: currentRate,
      total: currentPageCount,
    });
    yield put(setCurrentPage({productId, page}));
  }

  yield put(events.applyPageViewerRateCompleted({productId}));
}

function* applyScrollViewerRateCompleted({
  payload,
}: ReturnType<typeof events.applyScrollViewerRateCompleted>) {
  const {productId, wrapperStartY, wrapperBottomY} = payload;
  yield put(
    events.updateScrollViewerCurrentRateRequired({
      productId,
      wrapperStartY,
      wrapperBottomY,
    })
  );
}

function* applyPageViewerRateCompleted({
  payload,
}: ReturnType<typeof events.applyPageViewerRateCompleted>) {
  const {productId} = payload;
  yield put(events.updatePageViewerCurrentRateRequired({productId}));
}

function* updateScrollViewerCurrentRateRequired({
  payload,
}: ReturnType<typeof events.updateScrollViewerCurrentRateRequired>) {
  const {productId, wrapperStartY, wrapperBottomY} = payload;
  const {currentChapterId, chapterCount} = yield select((state: AppState) => ({
    currentChapterId:
      state.textViewer.viewData[payload.productId]?.currentChapterId,
    chapterCount: state.textViewer.viewData[payload.productId]?.chapterCount,
  }));

  const pageHeight = window.innerHeight;
  // 정밀도가 높은 장치면 소수값을 반환할 수 있어서, 스크롤 끝까지 가더라도 계산 결과가 10000이 안될 수 있습니다.
  // 정수값이 필요하니 MDN에 나온대로 반올림해서 사용합니다.
  // https://developer.mozilla.org/ko/docs/Web/API/Window/scrollY 참고
  const windowStartY = Math.round(window.scrollY);
  const calculatedRate = convertToLastViewRate({
    position: windowStartY - wrapperStartY + pageHeight,
    total: wrapperBottomY - wrapperStartY,
  });
  yield put(setCurrentRate({productId, rate: calculatedRate}));

  if (currentChapterId !== undefined && chapterCount !== undefined) {
    yield put(
      setIsDone({
        productId,
        isDone: checkTextViewerIsDone({
          rate: calculatedRate,
          currentChapterId,
          chapterCount,
        }),
      })
    );
  }
}

function* updatePageViewerCurrentRateRequired({
  payload,
}: ReturnType<typeof events.updatePageViewerCurrentRateRequired>) {
  const {productId} = payload;
  const {
    currentPage,
    currentPageCount,
    currentChapterId,
    chapterCount,
    isTwoPageLayout,
    readType,
  } = yield select((state: AppState) => ({
    currentPage: state.textViewer.viewData[payload.productId]?.currentPage,
    currentPageCount:
      state.textViewer.viewData[payload.productId]?.currentPageCount,
    currentChapterId:
      state.textViewer.viewData[payload.productId]?.currentChapterId,
    chapterCount: state.textViewer.viewData[payload.productId]?.chapterCount,
    isTwoPageLayout: state.textViewer.settings.isTwoPageLayout,
    readType: state.textViewer.settings.readType,
  }));
  const isMobile = requestContext.requestInfo.display.isMobile;

  const rate = convertToLastViewRate({
    position: currentPage,
    total: currentPageCount,
    isTwoPageLayout:
      isMobile || readType === TextViewerReadType.Scroll
        ? false
        : isTwoPageLayout,
  });

  yield put(setCurrentRate({productId, rate}));
  if (currentChapterId !== undefined && chapterCount !== undefined) {
    yield put(
      setIsDone({
        productId,
        isDone: checkTextViewerIsDone({
          rate,
          currentChapterId,
          chapterCount,
        }),
      })
    );
  }
}

function* redrawContentRequired({
  payload,
}: ReturnType<typeof events.redrawContentRequired>) {
  const {productId} = payload;
  yield put(setIsDrawing({productId, isDrawing: true}));
}

function* redrawContentCompleted({
  payload,
}: ReturnType<typeof events.redrawContentCompleted>) {
  const {
    productId,
    readType,
    wrapperStartY,
    wrapperBottomY,
    size,
    needScrollAdjust,
    isPreview,
  } = payload;
  const {chapterChangeDirection, isContentLoaded, currentPage} = yield select(
    (state: AppState) => ({
      chapterChangeDirection:
        state.textViewer.viewData[productId]?.chapterChangeDirection,
      isContentLoaded: state.textViewer.viewData[productId]?.isContentLoaded,
      currentPage: state.textViewer.viewData[productId]?.currentPage,
    })
  );

  yield put(setIsDrawing({productId, isDrawing: false}));

  if (
    readType === TextViewerReadType.Page ||
    wrapperStartY === undefined ||
    wrapperBottomY === undefined ||
    size === undefined
  ) {
    return;
  }

  // 스크롤을 통해 챕터 간 이동을 한 경우 스크롤 위치를 조정해줍니다.
  if (chapterChangeDirection) {
    if (chapterChangeDirection === 'down') {
      window.scroll(0, wrapperStartY);
      yield put(clearChapterChangeDirection({productId}));
    } else if (chapterChangeDirection === 'up') {
      window.scroll(0, wrapperBottomY);
      yield put(clearChapterChangeDirection({productId}));
    }
    // rate 갱신은 ScrollTextViewerProgressContainer에서 scroll 이벤트 발생 시 처리해줍니다.
    // 하지만 챕터 변경 시 scroll 이벤트 발생이 되지 않는 케이스가 있어서, 챕터 변경 시 rate를 직접 갱신해줍니다.
    yield put(
      events.updateScrollViewerCurrentRateRequired({
        productId,
        wrapperStartY,
        wrapperBottomY,
      })
    );
    return;
  }

  if (isContentLoaded && needScrollAdjust) {
    // 컨텐츠가 로딩된 후 유저가 임의로 챕터를 변경하는 경우 (시크바 조정 등) 렌더링이 완료된 후 스크롤 위치를 조정합니다.
    const pageHeight = size.height;
    const expectedPageScrollHeight = Math.min(
      wrapperStartY + pageHeight * (currentPage - 1),
      wrapperBottomY
    );

    if (!isPreview) {
      window.scroll(0, wrapperStartY + expectedPageScrollHeight);
    }

    yield put(
      events.updateScrollViewerCurrentRateRequired({
        productId,
        wrapperStartY,
        wrapperBottomY,
      })
    );
  }
}

function* textViewerErrorOccurred({
  payload,
}: ReturnType<typeof events.textViewerErrorOccurred>) {
  const {productId, error} = payload;
  yield put(setTextViewerError({productId, error}));
}

export default function* textViewerEventSaga() {
  yield all([
    takeEvery(EventType.TextViewerEntered, textViewerEntered),
    takeEvery(EventType.TextViewerDataLoadRequired, textViewerDataLoadRequired),
    takeEvery(
      EventType.TextViewerDataLoadCompleted,
      textViewerDataLoadCompleted
    ),
    takeEvery(
      EventType.TextViewerMetaLoadCompleted,
      textViewerMetaLoadCompleted
    ),
    takeEvery(EventType.TextViewerPreloadCompleted, textViewerPreloadCompleted),
    takeEvery(
      EventType.TextViewerChapterLoadCompleted,
      textViewerChapterLoadCompleted
    ),
    takeEvery(EventType.CurrentChapterChanged, currentChapterChanged),
    takeLatest(
      EventType.TextViewerContentLoadRequired,
      textViewerContentLoadRequired
    ),
    takeLatest(EventType.CurrentPageCountRequired, currentPageCountRequired),
    takeLatest(EventType.AllPageCountRequired, allPageCountRequired),
    takeLatest(
      EventType.TextViewerContentLoadCompleted,
      textViewerContentLoadCompleted
    ),
    takeLatest(
      EventType.ApplyPageViewerRateRequired,
      applyPageViewerRateRequired
    ),
    takeLatest(
      EventType.ApplyScrollViewerRateRequired,
      applyScrollViewerRateRequired
    ),
    takeLatest(
      EventType.ApplyScrollViewerRateCompleted,
      applyScrollViewerRateCompleted
    ),
    takeLatest(
      EventType.ApplyPageViewerRateCompleted,
      applyPageViewerRateCompleted
    ),
    takeLatest(
      EventType.UpdateScrollViewerCurrentRateRequired,
      updateScrollViewerCurrentRateRequired
    ),
    takeLatest(
      EventType.UpdatePageViewerCurrentRateRequired,
      updatePageViewerCurrentRateRequired
    ),
    takeEvery(EventType.RedrawContentRequired, redrawContentRequired),
    takeEvery(EventType.RedrawContentCompleted, redrawContentCompleted),
    takeEvery(EventType.TextViewerErrorOccurred, textViewerErrorOccurred),
  ]);
}
